import { useEffect, useRef, useState, type JSX } from 'react';
import { CircleHelp, ExternalLink, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { docIcon } from './docIcon';
import {
  APP_MESSAGE_SOURCE,
  isTrustedDocsEvent,
  parseDocsMessage,
  postToDocs,
  withDocsTheme,
} from './docsBridge';
import type { ManifestDoc } from './docsManifest';
import { readAppTheme, useAppTheme } from './useAppTheme';
import type { DocsManifestView } from './useDocsManifest';

/** The doc a docs path shows: the path without its query or hash. */
function docPathOf(path: string): string {
  return new URL(path, window.location.origin).pathname;
}

type DocsNavigation = Pick<DocsManifestView, 'docs' | 'flags'>;

interface NavGroup {
  title: string;
  docs: ManifestDoc[];
}

/** Titles the group for docs that sit in no docs section. */
const UNSECTIONED_TITLE = 'General';

/**
 * Docs grouped by their docs section (e.g. "Inventory", "Shadow AI",
 * "Integrations"), in the manifest's order, which follows the app's menu.
 * Docs in no section (top-level pages such as Get Started and Dashboard) go
 * first, under "General".
 */
function groupByCategory(docs: ManifestDoc[]): NavGroup[] {
  const groups = new Map<string | null, ManifestDoc[]>();
  for (const doc of docs) {
    const group = groups.get(doc.category);
    if (group) group.push(doc);
    else groups.set(doc.category, [doc]);
  }
  const sectioned = [...groups].flatMap(([title, groupDocs]) =>
    title === null ? [] : [{ title, docs: groupDocs }]
  );
  return [
    { title: UNSECTIONED_TITLE, docs: groups.get(null) ?? [] },
    ...sectioned,
  ];
}

interface FullDocsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where to open: a doc path, optionally with a #heading. */
  initialPath: string;
  /** The doc for the screen the user is on, if it has one. */
  screenDoc: ManifestDoc | null;
  manifest: DocsNavigation;
}

/**
 * Full docs (Mockup 2): the app renders the navigation from the manifest —
 * this screen's doc and its sections, then every other doc grouped by its
 * docs section, in the app's menu order — and frames the docs page next to
 * it. The docs get the app's theme on first load (URL parameter) and its
 * flags, theme changes, page changes and section jumps over postMessage, and
 * report in-frame navigation back so the navigation, title and "Open in new
 * tab" follow.
 */
export function FullDocsDialog({
  open,
  onOpenChange,
  initialPath,
  screenDoc,
  manifest,
}: FullDocsDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex h-[calc(100vh-4rem)] max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(1600px,calc(100%-4rem))]"
      >
        {/* Mounted per opening, so each opening starts at initialPath. */}
        <FullDocsContent
          initialPath={initialPath}
          screenDoc={screenDoc}
          manifest={manifest}
        />
      </DialogContent>
    </Dialog>
  );
}

function FullDocsContent({
  initialPath,
  screenDoc,
  manifest,
}: {
  initialPath: string;
  screenDoc: ManifestDoc | null;
  manifest: DocsNavigation;
}): JSX.Element {
  const theme = useAppTheme();
  const frameRef = useRef<HTMLIFrameElement>(null);
  // The theme goes into the frame URL only for the first page load: a later
  // theme change travels over postMessage, so it never reloads the page the
  // user is reading.
  const [frameSrc, setFrameSrc] = useState(() =>
    withDocsTheme(initialPath, readAppTheme())
  );
  // What the frame shows, as the docs report it.
  const [currentPath, setCurrentPath] = useState(initialPath);
  // Counts "ready" messages: every page load in the frame, including one the
  // app did not start (a reload), gets the flags and theme. 0 = not loaded yet.
  const [loads, setLoads] = useState(0);
  // A section picked before the page was ready; shown once it is.
  const pendingAnchor = useRef<string | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      if (!isTrustedDocsEvent(event, frameRef.current)) return;
      const message = parseDocsMessage(event.data);
      if (!message) return;
      if (message.type === 'navigated') {
        setCurrentPath(message.path);
        return;
      }
      setLoads(count => count + 1);
      const anchor = pendingAnchor.current;
      if (anchor) {
        pendingAnchor.current = null;
        postToDocs(frameRef.current, {
          source: APP_MESSAGE_SOURCE,
          type: 'scroll',
          anchor,
        });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    // Not while the flags load: the docs would read every flag as off.
    if (loads === 0 || manifest.flags === null) return;
    postToDocs(frameRef.current, {
      source: APP_MESSAGE_SOURCE,
      type: 'flags',
      flags: manifest.flags,
    });
  }, [loads, manifest.flags]);

  useEffect(() => {
    if (loads === 0) return;
    postToDocs(frameRef.current, {
      source: APP_MESSAGE_SOURCE,
      type: 'theme',
      theme,
    });
  }, [loads, theme]);

  const openDoc = (path: string): void => {
    pendingAnchor.current = null;
    setCurrentPath(path);
    if (loads > 0) {
      // Client-side, so the page and its flags and theme stay put.
      postToDocs(frameRef.current, {
        source: APP_MESSAGE_SOURCE,
        type: 'navigate',
        path,
      });
    } else {
      setFrameSrc(withDocsTheme(path, readAppTheme()));
    }
  };

  const showSection = (anchor: string): void => {
    if (loads > 0) {
      postToDocs(frameRef.current, {
        source: APP_MESSAGE_SOURCE,
        type: 'scroll',
        anchor,
      });
    } else {
      pendingAnchor.current = anchor;
    }
  };

  const activePath = docPathOf(currentPath);
  const activeDoc = manifest.docs.find(doc => doc.path === activePath) ?? null;
  const groups: NavGroup[] = [
    { title: 'This screen', docs: screenDoc ? [screenDoc] : [] },
    ...groupByCategory(manifest.docs.filter(doc => doc.key !== screenDoc?.key)),
  ];

  return (
    <>
      <div className="flex items-center gap-3 border-b px-5 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
          <FileText aria-hidden="true" className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">Documentation</p>
          <DialogTitle className="truncate text-base">
            {activeDoc?.title ?? 'Kirin documentation'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Product documentation, with navigation on the left.
          </DialogDescription>
        </div>
        <span className="hidden shrink-0 items-center gap-1 rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700 sm:inline-flex dark:bg-violet-500/15 dark:text-violet-300">
          <CircleHelp aria-hidden="true" className="h-3.5 w-3.5" />
          Opened from Help
        </span>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <a
              href={withDocsTheme(currentPath, theme)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink aria-hidden="true" />
              Open in new tab
            </a>
          </Button>
          <DialogClose asChild>
            <Button variant="outline" size="sm">
              Close
            </Button>
          </DialogClose>
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <nav
          aria-label="Documentation"
          className="w-64 shrink-0 overflow-y-auto border-r px-3 py-2"
        >
          {groups
            .filter(group => group.docs.length > 0)
            .map(group => (
              <div key={group.title} className="mt-3">
                <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.title}
                </p>
                {group.docs.map(doc => {
                  const Icon = docIcon(doc);
                  const active = doc.key === activeDoc?.key;
                  return (
                    <div key={doc.key}>
                      <button
                        type="button"
                        aria-current={active ? 'page' : undefined}
                        onClick={() => openDoc(doc.path)}
                        className={cn(
                          'flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm',
                          active
                            ? 'bg-violet-50 font-medium text-violet-700 dark:bg-violet-500/15 dark:text-violet-300'
                            : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                        )}
                      >
                        <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                        <span className="truncate">{doc.title}</span>
                      </button>
                      {active && doc.sections.length > 0 && (
                        <ul aria-label={`${doc.title} sections`}>
                          {doc.sections.map(section => (
                            <li key={section.id}>
                              <button
                                type="button"
                                onClick={() => showSection(section.id)}
                                className="block w-full cursor-pointer truncate rounded-md py-1 pl-9 pr-2 text-left text-sm font-medium text-violet-700 hover:bg-muted dark:text-violet-300"
                              >
                                {section.title}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
        </nav>
        <iframe
          ref={frameRef}
          title="Kirin documentation page"
          src={frameSrc}
          className="h-full min-w-0 flex-1 border-0 bg-background"
        />
      </div>
    </>
  );
}
